import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./mockup-app.css";
import {
    MOCK_NAV,
    MOCK_TICKETS,
    MOCK_USER,
    MOCK_WORKSPACE,
    MOCK_DEPARTMENTS,
    MOCK_USERS,
    MOCK_EMAILS,
    buildAssigneeOptions,
    filterMockTickets,
    MOCK_UNASSIGNED_DEPARTMENT,
    searchMockApp,
    TICKET_STATUS_OPTIONS,
    TICKET_PRIORITY_OPTIONS,
} from "./mockData";
import MockSidebar from "./MockSidebar";
import MockDashboard from "./MockDashboard";
import MockTickets from "./MockTickets";
import MockTicketDetail from "./MockTicketDetail";
import MockUsers from "./MockUsers";
import MockDepartments from "./MockDepartments";
import MockEmails from "./MockEmails";
import MockReports from "./MockReports";
import MockEmbedForms from "./MockEmbedForms";
import MockTopBar from "./MockTopBar";
import MockMainNavbar from "./MockMainNavbar";
import MockPlatformRail from "./MockPlatformRail";
import MockConfirm from "./MockConfirm";
import Icon from "../Icon";

const ClaritickMockup = ({ variant = "full", defaultView = "dashboard" }) => {
    const [view, setView] = useState(defaultView);
    const [tickets, setTickets] = useState(MOCK_TICKETS);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketSubmenu, setTicketSubmenu] = useState("all");
    const [statusFilter, setStatusFilter] = useState([]);
    const [departmentFilter, setDepartmentFilter] = useState([]);
    const [priorityFilter, setPriorityFilter] = useState([]);
    const [assigneeFilter, setAssigneeFilter] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [globalSearch, setGlobalSearch] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const [departments, setDepartments] = useState(MOCK_DEPARTMENTS);
    const [users, setUsers] = useState(MOCK_USERS);
    const [emails, setEmails] = useState(MOCK_EMAILS);
    const [expandedMenus, setExpandedMenus] = useState({ tickets: true });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(null);

    useEffect(() => {
        const validNames = new Set(departments.map((d) => d.name));
        setDepartmentFilter((prev) => {
            const next = prev.filter((name) => validNames.has(name));
            return next.length === prev.length ? prev : next;
        });
    }, [departments]);

    const showConfirm = useCallback((opts) => {
        setConfirmDialog(opts);
    }, []);

    const closeConfirm = useCallback(() => {
        setConfirmDialog(null);
    }, []);

    const isCompact = variant === "compact";
    const navItems = isCompact
        ? MOCK_NAV.filter((n) => ["dashboard", "tickets"].includes(n.id))
        : MOCK_NAV;

    const assigneeOptions = useMemo(() => buildAssigneeOptions(users), [users]);

    const filteredTickets = useMemo(
        () => filterMockTickets(
            tickets,
            {
                submenu: ticketSubmenu,
                status: statusFilter,
                department: departmentFilter,
                priority: priorityFilter,
                assignee: assigneeFilter,
                search: searchQuery,
            },
            MOCK_USER.initials
        ),
        [tickets, ticketSubmenu, statusFilter, departmentFilter, priorityFilter, assigneeFilter, searchQuery]
    );

    const globalSearchResults = useMemo(
        () => searchMockApp(globalSearch, { tickets, users, emails, departments }),
        [globalSearch, tickets, users, emails, departments]
    );

    const handleSelectTicket = useCallback((ticket) => {
        setSelectedTicket(ticket);
        setView("ticket-detail");
    }, []);

    const handleStatusChange = useCallback((ticketId, status) => {
        const opt = TICKET_STATUS_OPTIONS.find((o) => o.value === status);
        const statusLabel = opt?.label || status;
        setTickets((prev) =>
            prev.map((t) => (t.id === ticketId ? { ...t, status, statusLabel } : t))
        );
        setSelectedTicket((prev) =>
            prev && prev.id === ticketId ? { ...prev, status, statusLabel } : prev
        );
    }, []);

    const handlePriorityChange = useCallback((ticketId, priorityKey) => {
        const opt = TICKET_PRIORITY_OPTIONS.find((o) => o.value === priorityKey);
        const priority = opt?.label || priorityKey;
        setTickets((prev) =>
            prev.map((t) => (t.id === ticketId ? { ...t, priorityKey, priority } : t))
        );
        setSelectedTicket((prev) =>
            prev && prev.id === ticketId ? { ...prev, priorityKey, priority } : prev
        );
    }, []);

    const handleDepartmentChange = useCallback((ticketId, departmentName) => {
        const dept = departments.find((d) => d.name === departmentName);
        const deptColor = departmentName === MOCK_UNASSIGNED_DEPARTMENT.name
            ? MOCK_UNASSIGNED_DEPARTMENT.color
            : (dept?.color || "#6b7280");
        setTickets((prev) =>
            prev.map((t) => (t.id === ticketId ? { ...t, department: departmentName, deptColor } : t))
        );
        setSelectedTicket((prev) =>
            prev && prev.id === ticketId
                ? { ...prev, department: departmentName, deptColor }
                : prev
        );
    }, [departments]);

    const handleAssigneeChange = useCallback((ticketId, assignee) => {
        const next = assignee || null;
        setTickets((prev) =>
            prev.map((t) => (t.id === ticketId ? { ...t, assignee: next } : t))
        );
        setSelectedTicket((prev) =>
            prev && prev.id === ticketId ? { ...prev, assignee: next } : prev
        );
    }, []);

    const handleToggleMenu = useCallback((menuId) => {
        setExpandedMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
    }, []);

    const handleNavClick = useCallback((id) => {
        closeConfirm();
        setView(id);
        if (id !== "tickets" && id !== "ticket-detail") {
            setSelectedTicket(null);
            setExpandedMenus((prev) => ({ ...prev, tickets: false }));
        }
        if (id === "tickets") {
            setExpandedMenus((prev) => ({ ...prev, tickets: true }));
        }
        setSidebarOpen(false);
    }, [closeConfirm]);

    const handleSubmenuClick = useCallback((parentId, childId) => {
        if (parentId === "tickets") {
            setTicketSubmenu(childId);
            setView("tickets");
            setSelectedTicket(null);
            setExpandedMenus((prev) => ({ ...prev, tickets: true }));
        }
        setSidebarOpen(false);
    }, []);

    const handleGlobalSearchChange = useCallback((value) => {
        setGlobalSearch(value);
        setSearchOpen(true);
    }, []);

    const handleSearchResultSelect = useCallback((result) => {
        setGlobalSearch("");

        if (result.type === "ticket") {
            const ticket = tickets.find((t) => t.id === result.ticketId);
            if (ticket) {
                setSelectedTicket(ticket);
                setView("ticket-detail");
                setExpandedMenus((prev) => ({ ...prev, tickets: true }));
            }
            return;
        }

        handleNavClick(result.view);
    }, [tickets, handleNavClick]);

    const handleAddDepartment = useCallback(({ name, color }) => {
        setDepartments((prev) => {
            const nextId = prev.reduce((max, d) => Math.max(max, d.id), 0) + 1;
            return [...prev, { id: nextId, name, color, users: 0, emails: 0, tickets: 0 }];
        });
    }, []);

    const handleUpdateDepartment = useCallback((deptId, { name, color }) => {
        setDepartments((prev) => {
            const existing = prev.find((d) => d.id === deptId);
            if (!existing) return prev;
            const oldName = existing.name;

            setTickets((tickets) =>
                tickets.map((t) =>
                    t.department === oldName ? { ...t, department: name, deptColor: color } : t
                )
            );
            setEmails((emails) =>
                emails.map((e) =>
                    e.department === oldName ? { ...e, department: name, deptColor: color } : e
                )
            );
            setDepartmentFilter((prev) =>
                prev.map((filterName) => (filterName === oldName ? name : filterName))
            );

            return prev.map((d) => (d.id === deptId ? { ...d, name, color } : d));
        });
    }, []);

    const handleDeleteDepartment = useCallback((deptId) => {
        setDepartments((prev) => {
            if (prev.length <= 1) return prev;
            const removed = prev.find((d) => d.id === deptId);
            if (!removed) return prev;
            const remaining = prev.filter((d) => d.id !== deptId);
            const emailFallback = remaining[0];
            if (!emailFallback) return prev;

            setTickets((tickets) =>
                tickets.map((t) =>
                    t.department === removed.name
                        ? {
                            ...t,
                            department: MOCK_UNASSIGNED_DEPARTMENT.name,
                            deptColor: MOCK_UNASSIGNED_DEPARTMENT.color,
                        }
                        : t
                )
            );
            setEmails((emails) =>
                emails.map((e) =>
                    e.department === removed.name
                        ? {
                            ...e,
                            department: emailFallback.name,
                            deptColor: emailFallback.color,
                        }
                        : e
                )
            );
            setDepartmentFilter((prev) => prev.filter((name) => name !== removed.name));

            return prev.filter((d) => d.id !== deptId);
        });
    }, []);

    const handleAddUser = useCallback(({ name, email, title, role }) => {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const d = new Date();
        const joined = `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
        setUsers((prev) => {
            const nextId = prev.reduce((max, u) => Math.max(max, u.id), 0) + 1;
            return [...prev, { id: nextId, name, email, title, role, accepted: true, joined }];
        });
    }, []);

    const handleUpdateUser = useCallback((userId, data) => {
        setUsers((prev) =>
            prev.map((u) => (u.id === userId ? { ...u, ...data } : u))
        );
    }, []);

    const handleDeleteUser = useCallback((userId) => {
        setUsers((prev) => prev.filter((u) => u.id !== userId));
    }, []);

    const handleAddEmail = useCallback(({ name, address, department, deptColor }) => {
        setEmails((prev) => {
            const nextId = prev.reduce((max, e) => Math.max(max, e.id), 0) + 1;
            return [...prev, { id: nextId, name, address, department, deptColor, received: 0 }];
        });
    }, []);

    const handleUpdateEmail = useCallback((emailId, data) => {
        setEmails((prev) =>
            prev.map((e) => (e.id === emailId ? { ...e, ...data } : e))
        );
    }, []);

    const handleDeleteEmail = useCallback((emailId) => {
        setEmails((prev) => prev.filter((e) => e.id !== emailId));
    }, []);

    const handleAddTicket = useCallback((data = {}) => {
        setTickets((prev) => {
            const nextId = prev.reduce((max, t) => Math.max(max, t.id), 0) + 1;
            const dept = departments.find((d) => d.name === data.department) || departments[0];
            const statusOpt = TICKET_STATUS_OPTIONS.find((o) => o.value === (data.status || "open"));
            const priorityOpt = TICKET_PRIORITY_OPTIONS.find((o) => o.value === (data.priorityKey || "medium"));
            const description = data.description || "Describe your issue here…";
            const title = data.title || "New support request";
            return [
                {
                    id: nextId,
                    ticketId: `demo${nextId}`,
                    title,
                    preview: description.length > 80 ? `${description.slice(0, 80)}…` : description,
                    description,
                    status: data.status || "open",
                    statusLabel: statusOpt?.label || "Open",
                    department: dept?.name || "Customer Support",
                    deptColor: dept?.color || "#92400e",
                    priority: priorityOpt?.label || "Medium",
                    priorityKey: data.priorityKey || "medium",
                    typeKey: data.typeKey || "",
                    tagKey: data.tagKey || "",
                    assignee: data.assignee || null,
                    replied: "-",
                    created: "just now",
                    createdBy: "Guest",
                    due: "-",
                    messages: [{ from: "customer", text: description }],
                },
                ...prev,
            ];
        });
    }, [departments]);

    const urlPath = useMemo(() => {
        if (view === "ticket-detail") return "ticket";
        if (view === "embed-forms") return "embed-ticket-forms";
        return view;
    }, [view]);

    const renderMain = () => {
        if (view === "ticket-detail" && selectedTicket) {
            const live = tickets.find((t) => t.id === selectedTicket.id) || selectedTicket;
            return (
                <MockTicketDetail
                    ticket={live}
                    tickets={tickets}
                    departments={departments}
                    assigneeOptions={assigneeOptions}
                    onBack={() => {
                        setView("tickets");
                        setSelectedTicket(null);
                    }}
                    onSelectTicket={(t) => {
                        setSelectedTicket(t);
                        setView("ticket-detail");
                    }}
                    onStatusChange={handleStatusChange}
                    onDepartmentChange={handleDepartmentChange}
                    onPriorityChange={handlePriorityChange}
                    onAssigneeChange={handleAssigneeChange}
                />
            );
        }

        switch (view) {
            case "tickets":
                return (
                    <MockTickets
                        tickets={filteredTickets}
                        totalTickets={tickets.length}
                        selectedId={selectedTicket?.id}
                        onSelect={handleSelectTicket}
                        statusFilter={statusFilter}
                        departmentFilter={departmentFilter}
                        priorityFilter={priorityFilter}
                        assigneeFilter={assigneeFilter}
                        searchQuery={searchQuery}
                        onStatusFilterChange={setStatusFilter}
                        onDepartmentFilterChange={setDepartmentFilter}
                        onPriorityFilterChange={setPriorityFilter}
                        onAssigneeFilterChange={setAssigneeFilter}
                        onSearchChange={setSearchQuery}
                        departments={departments}
                        assigneeOptions={assigneeOptions}
                        onStatusChange={handleStatusChange}
                        onDepartmentChange={handleDepartmentChange}
                        onPriorityChange={handlePriorityChange}
                        onAssigneeChange={handleAssigneeChange}
                        onAddTicket={handleAddTicket}
                    />
                );
            case "users":
                return (
                    <MockUsers
                        users={users}
                        onAddUser={handleAddUser}
                        onUpdateUser={handleUpdateUser}
                        onDeleteUser={handleDeleteUser}
                        showConfirm={showConfirm}
                    />
                );
            case "departments":
                return (
                    <MockDepartments
                        departments={departments}
                        onAddDepartment={handleAddDepartment}
                        onUpdateDepartment={handleUpdateDepartment}
                        onDeleteDepartment={handleDeleteDepartment}
                        showConfirm={showConfirm}
                    />
                );
            case "emails":
                return (
                    <MockEmails
                        emails={emails}
                        departments={departments}
                        onAddEmail={handleAddEmail}
                        onUpdateEmail={handleUpdateEmail}
                        onDeleteEmail={handleDeleteEmail}
                        showConfirm={showConfirm}
                    />
                );
            case "reports":
                return <MockReports />;
            case "embed-forms":
                return <MockEmbedForms departments={departments} />;
            default:
                return <MockDashboard departments={departments} />;
        }
    };

    return (
        <div className={`clk-mockup clk-mockup--${variant}${variant === "full" ? " clk-mockup--app" : ""}`}>
            {variant === "full" ? (
                <div className="clk-mockup-app-shell">
                    <MockTopBar />
                    <div className={`clk-mockup-app-frame${sidebarOpen ? " is-sidebar-open" : ""}`}>
                        <button
                            type="button"
                            className="clk-mockup-side-backdrop"
                            aria-label="Close navigation"
                            tabIndex={sidebarOpen ? 0 : -1}
                            onClick={() => setSidebarOpen(false)}
                        />
                        <MockSidebar
                            navItems={navItems}
                            view={view}
                            ticketSubmenu={ticketSubmenu}
                            expandedMenus={expandedMenus}
                            onToggleMenu={handleToggleMenu}
                            onNavClick={handleNavClick}
                            onSubmenuClick={handleSubmenuClick}
                            user={MOCK_USER}
                            workspace={MOCK_WORKSPACE}
                            isCompact={isCompact}
                            onNavToggle={() => setSidebarOpen((open) => !open)}
                            sidebarOpen={sidebarOpen}
                        />
                        <div className="clk-mockup-app-content">
                            <MockMainNavbar
                                searchQuery={globalSearch}
                                onSearchChange={handleGlobalSearchChange}
                                searchResults={globalSearchResults}
                                onSelectResult={handleSearchResultSelect}
                                isSearchOpen={searchOpen}
                                onSearchFocus={() => setSearchOpen(true)}
                                onSearchBlur={() => setSearchOpen(false)}
                                onNavToggle={() => setSidebarOpen((open) => !open)}
                                sidebarOpen={sidebarOpen}
                            />
                            <main className="clk-mockup-main">{renderMain()}</main>
                        </div>
                        <MockPlatformRail />
                        {confirmDialog && (
                            <MockConfirm
                                title={confirmDialog.title}
                                message={confirmDialog.message}
                                confirmLabel={confirmDialog.confirmLabel}
                                cancelLabel={confirmDialog.cancelLabel}
                                onConfirm={() => {
                                    confirmDialog.onConfirm?.();
                                    closeConfirm();
                                }}
                                onCancel={closeConfirm}
                            />
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="clk-window clk-mockup-window">
                        <div className="clk-window-bar">
                            <i /><i /><i />
                            <span className="clk-window-url">claritick.com/{urlPath}</span>
                        </div>
                        <div className={`clk-mockup-body${sidebarOpen ? " is-sidebar-open" : ""}`}>
                            <button
                                type="button"
                                className="clk-mockup-nav-toggle"
                                onClick={() => setSidebarOpen((open) => !open)}
                                aria-expanded={sidebarOpen}
                                aria-label="Toggle navigation"
                            >
                                <Icon name="menu" size={18} />
                            </button>
                            <button
                                type="button"
                                className="clk-mockup-side-backdrop"
                                aria-label="Close navigation"
                                tabIndex={sidebarOpen ? 0 : -1}
                                onClick={() => setSidebarOpen(false)}
                            />
                            <MockSidebar
                                navItems={navItems}
                                view={view}
                                ticketSubmenu={ticketSubmenu}
                                expandedMenus={expandedMenus}
                                onToggleMenu={handleToggleMenu}
                                onNavClick={handleNavClick}
                                onSubmenuClick={handleSubmenuClick}
                                user={MOCK_USER}
                                workspace={MOCK_WORKSPACE}
                                isCompact={isCompact}
                                onNavToggle={() => setSidebarOpen((open) => !open)}
                                sidebarOpen={sidebarOpen}
                            />
                            <main className="clk-mockup-main">{renderMain()}</main>
                            {confirmDialog && (
                                <MockConfirm
                                    title={confirmDialog.title}
                                    message={confirmDialog.message}
                                    confirmLabel={confirmDialog.confirmLabel}
                                    cancelLabel={confirmDialog.cancelLabel}
                                    onConfirm={() => {
                                        confirmDialog.onConfirm?.();
                                        closeConfirm();
                                    }}
                                    onCancel={closeConfirm}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ClaritickMockup;
